import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Bell, Send, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatOrgRoleLabel } from '../../lib/orgRoles';
import { API_URL } from '../../config';

export default function PingTeamModal({ isOpen, onClose, document, members, currentOrg }) {
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!selectedMemberId || !document?.id) return;
    
    setSending(true);
    try {
      // Send notification to team member
      await axios.post(`${API_URL}/organizations/${currentOrg?.id}/notifications`, {
        recipient_id: selectedMemberId,
        type: 'document_ping',
        document_id: document.id,
        document_title: document.title,
        message: `You've been notified about the document "${document.title}"`
      });
      
      setSent(true);
      toast.success('Notification sent!');
      setTimeout(() => {
        setSent(false);
        setSelectedMemberId('');
        onClose();
      }, 1500);
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="ping-team-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Bell className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </div>
            <span>Ping team member</span>
          </DialogTitle>
          <DialogDescription>
            Select team members to notify them about this document.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
              Select team member
            </label>
            <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
              <SelectTrigger className="w-full" data-testid="ping-member-select">
                <SelectValue placeholder="Choose a team member..." />
              </SelectTrigger>
              <SelectContent>
                {members && members.length > 0 ? (
                  members.map((member) => (
                    <SelectItem key={member.id} value={member.user_id || member.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="w-6 h-6">
                          <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                            {getInitials(member.user_name || member.user_email)}
                          </AvatarFallback>
                        </Avatar>
                        <span>{member.user_name || member.user_email}</span>
                        <span className="text-slate-400 text-xs">
                          ({formatOrgRoleLabel(member.role)})
                        </span>
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-4 text-center text-sm text-slate-500">
                    No team members found
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              <strong>Document:</strong> {document?.title || 'Untitled'}
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={!selectedMemberId || sending || sent}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-ping-button"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : sent ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Sent!
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Notification
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
